import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild, forwardRef } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { S3FileService } from '../../../core/services/s3-file.service';
import { ImageCropUploadComponent, ImageTile } from '../../image-crop-upload/image-crop-upload.component';

@Component({
  selector: 'app-single-image-input',
  standalone: false,
  templateUrl: './single-image-input.html',
  styleUrl: './single-image-input.scss',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SingleImageInput), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => SingleImageInput), multi: true },
  ],
})
export class SingleImageInput implements ControlValueAccessor, Validator, OnDestroy {
  @Input() label = 'Image';
  @Input() addLabel = 'Add image';
  @Input() required = false;
  @Input() sidebarSafe = false;
  @Input() allowRemove = true;
  @Input() triggerClass = 'btn p-1';
  @Output() busyChange = new EventEmitter<boolean>();
  @ViewChild(ImageCropUploadComponent) cropper!: ImageCropUploadComponent;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  key = '';
  preview = '';
  error = '';
  disabled = false;
  busy = false;
  private previewUnavailable = false;
  private sequence = 0;
  private destroyed = false;
  private changed = (_value: string) => {};
  private touched = () => {};
  private validatorChanged = () => {};

  constructor(private readonly files: S3FileService, private readonly cdr: ChangeDetectorRef) {}

  writeValue(value: string | null): void {
    const key = String(value || '');
    if (key === this.key && this.preview) return;
    this.key = key;
    this.preview = '';
    this.error = '';
    this.previewUnavailable = false;
    const sequence = ++this.sequence;
    // CVA patches must never propagate a value change or invalidate Angular's
    // initial view check. Cached/private preview resolution is service-owned.
    queueMicrotask(() => { void this.loadPreview(key, sequence); });
  }

  private async loadPreview(key: string, sequence: number): Promise<void> {
    if (this.destroyed || sequence !== this.sequence) return;
    this.setBusy(!!key);
    try {
      const preview = key ? await firstValueFrom(this.files.getFileUrlValue(key)) : '';
      if (sequence !== this.sequence || this.destroyed) return;
      this.preview = preview || '';
      this.previewUnavailable = !!key && !preview;
      this.error = this.previewUnavailable ? 'The saved image could not be loaded. Retry or choose a replacement; its saved key is unchanged.' : '';
    } catch {
      if (sequence !== this.sequence || this.destroyed) return;
      this.previewUnavailable = true;
      this.error = 'The saved image could not be loaded. Choose a replacement; its saved key is unchanged.';
    } finally {
      if (sequence === this.sequence && !this.destroyed) {
        this.setBusy(false);
        this.validatorChanged();
        this.cdr.detectChanges();
      }
    }
  }

  registerOnChange(fn: (value: string) => void): void { this.changed = fn; }
  registerOnTouched(fn: () => void): void { this.touched = fn; }
  markTouched(): void { this.touched(); }
  setDisabledState(disabled: boolean): void { this.disabled = disabled; }
  registerOnValidatorChange(fn: () => void): void { this.validatorChanged = fn; }
  validate(_control: AbstractControl): ValidationErrors | null {
    if (this.required && !this.key) return { required: { message: `${this.label} is required.` } };
    return this.previewUnavailable ? { imageUnavailable: { message: 'The saved image could not be loaded. Choose a replacement or remove it.' } } : null;
  }

  choose(): void {
    if (!this.disabled && !this.busy) this.fileInput.nativeElement.click();
  }

  async selected(event: Event): Promise<void> {
    if (this.disabled || this.busy) return;
    this.touched();
    this.error = '';
    this.setBusy(true);
    try { await this.cropper.onImagesSelected(event); }
    catch { this.error = 'Image upload failed. The previous image is unchanged.'; }
    finally { if (!this.destroyed) { this.setBusy(false); this.cdr.detectChanges(); } }
  }

  uploaded(image: ImageTile): void {
    if (this.destroyed) return;
    if (!image.key || image.error) {
      this.error = 'Image upload failed. The previous image is unchanged.';
    } else {
      this.sequence++;
      this.key = image.key;
      this.preview = image.preview || '';
      this.previewUnavailable = false;
      this.changed(this.key);
      this.validatorChanged();
    }
    this.cdr.detectChanges();
  }

  remove(): void {
    if (this.disabled || this.busy) return;
    this.sequence++;
    this.key = this.preview = this.error = '';
    this.previewUnavailable = false;
    this.changed('');
    this.touched();
    this.validatorChanged();
  }

  private setBusy(value: boolean): void { this.busy = value; this.busyChange.emit(value); }
  ngOnDestroy(): void { this.destroyed = true; this.sequence++; }
}
